// src/hooks/useFrontAuth.js
//
// Giriş yapmış admin kullanıcının kendi rolü + frontAuth yetkilerini AuthContext'ten
// okuyan ve bileşenlerde kullanımı kolay bir API döndüren hook.
//
// Kullanım:
//   const { can, canView, role, isSuperadmin } = useFrontAuth();
//   {can("firm-list", "add") && <AddEntityButton ... />}

import { useMemo } from "react";
import { useAuth } from "../AuthContext";
import { getAdminPages } from "../pageConfig";
import {
  ALL_ACCESS,
  normalizeRole,
  isSuperadmin as isSuperadminRole,
  buildDefaultPermissions,
  can as canFn,
  canView as canViewFn,
  canManagePermissions as canManagePermissionsFn,
  mergeMissingPagePermissions,
} from "../utils/frontAuth";

export function useFrontAuth() {
  const { frontAuthRole, frontAuth } = useAuth();

  return useMemo(() => {
    const role = normalizeRole(frontAuthRole);
    const superadmin = isSuperadminRole(role);

    // superadmin → ALL_ACCESS (sınırsız). Aksi halde kayıtlı yetki; yoksa rol default'u.
    const hasStored =
      frontAuth && typeof frontAuth === "object" && Object.keys(frontAuth).length > 0;
    // hasStored: kayıtlı yetki objesi sonradan eklenen sayfaların anahtarlarını içermeyebilir;
    // eksik anahtarlar rol default'undan tamamlanır (bkz. mergeMissingPagePermissions).
    const permissions = superadmin
      ? ALL_ACCESS
      : hasStored
        ? mergeMissingPagePermissions(frontAuth, role, getAdminPages())
        : buildDefaultPermissions(role, getAdminPages());

    const can = (pageKey, capability = "view") => canFn(permissions, pageKey, capability);
    const canView = (pageKey) => canViewFn(permissions, pageKey);
    const canManagePermissions = canManagePermissionsFn(permissions, role);

    return {
      role,
      permissions,
      isSuperadmin: superadmin,
      can,
      canView,
      canManagePermissions,
    };
  }, [frontAuthRole, frontAuth]);
}

export default useFrontAuth;
